import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Divider, Grid, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import * as Yup from 'yup';
import { FieldArray, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';

import useNotification from '../../../core/hooks/use_notification';
import { getReservationOrderAction, getReservationOrderState } from '../../../features/order/get_reservation_order';
import { getReservationOrderNotificationData, returnReservationNotificationData } from '../../../core/utils/notification_utils';
import { Button, SubmitButton } from '../../common/button';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import RadioGroupFormik from '../../common/radio_group';
import { yesNoOptions } from '../../common/form_utils';
import { SelectFieldFormik } from '../../common/select_field';
import { getReturnReasonOptions } from '../../common/utils/common';
import TextFieldFormik from '../../common/text_field';
import { returnCustomerReservationAction, returnCustomerReservationState } from '../../../features/reservation/return_customer_reservation';
import { getReservationStatusColor } from '../../common/utils/reservation_utils';

const ReturnReservationDialog = ({ reservation, refreshList }) => {
    const dispatch = useDispatch();
    const { showErrorMessage, showNotification } = useNotification();
    const { enqueueSnackbar } = useSnackbar();

    const getReservationOrder = useSelector(getReservationOrderState);
    const returnCustomerReservation = useSelector(returnCustomerReservationState);

    const [open, setOpen] = useState(false);
    const [initialValues, setInitialValues] = useState({
        allPositions: true,
        reason: '',
        positions: [],
    });

    const schema = Yup.object().shape({
        allPositions: Yup.boolean().required(),
        reason: Yup.string().required(),
        positions: Yup.array().notRequired(),
    });

    useEffect(() => {
        if (open) {
            dispatch(getReservationOrderAction({
                id: 'R' + reservation?.reservationOrderId,
                customerId: reservation?.customer?.customerId
            }))
                .then(response => {
                    showErrorMessage(getReservationOrderNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.REJECTED) onClose();

                    const newPositions = reservation?.items?.map(item => {
                        const reservationOrderProduct = response?.payload?.products?.find(orderProduct => orderProduct?.id === item?.reservationOrderProductId);

                        return {
                            productReservationId: item?.productReservationId,
                            productName: item?.product?.productName,
                            sellingPrice: reservationOrderProduct?.sellingPrice?.toFixed(2) || '0.00',
                            quantity: item?.quantity,
                            newQuantity: item?.quantity,
                            isReturned: item?.returned,
                        };
                    });

                    setInitialValues(prevState => ({ ...prevState, positions: newPositions }));
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reservation?.customer?.customerId, reservation?.reservationOrderId, dispatch]);

    const onClose = () => setOpen(false);

    const onSubmit = (values) => {
        let form = {
            reason: values?.reason,
            allPositions: values?.allPositions
        };
        let data = {
            customerId: reservation?.customer?.customerId,
            reservationId: reservation?.reservationId,
            form
        };

        if (values?.allPositions) {
            dispatch(returnCustomerReservationAction(data))
                .then(response => {
                    showNotification(returnReservationNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        refreshList();
                        onClose();
                    }
                });
        } else {
            const positions = values?.positions
                ?.map(position => ({ ...position, quantityToReturn: position?.quantity - position?.newQuantity }))
                ?.map(position => ({
                    quantityToReturn: position?.quantityToReturn,
                    productReservationId: position?.productReservationId
                }))
                ?.filter(position => position?.quantityToReturn !== 0);

            form = { ...form, positions };
            data = { ...data, form };

            if (positions.length === 0) {
                enqueueSnackbar('Nie naniesiono żadnych zmian', { variant: 'error' });
                return;
            }

            dispatch(returnCustomerReservationAction(data))
                .then(response => {
                    showNotification(returnReservationNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        refreshList();
                        onClose();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Utwórz zwrot">
                <IconButton size="small" onClick={ () => setOpen(true) }>
                    <MoneyOffIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="lg">
                <Formik
                    initialValues={ initialValues }
                    enableReinitialize
                    validationSchema={ schema }
                    validateOnChange={ false }
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }>
                    { (formik) =>
                        <Box component={ Form }>
                            { getReservationOrder?.loading && <LinearProgress/> }

                            { getReservationOrder?.data && (
                                <>
                                    <DialogTitle>
                                        <Grid container alignItems="center" justifyContent="space-between">
                                            <Grid>
                                                <Typography variant="h6" fontWeight="bold">
                                                    Zwrot do rezerwacji { reservation?.reservationId }
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Data rezerwacji: <b>{ reservation?.date }</b>
                                                </Typography>
                                            </Grid>
                                            <Chip
                                                size="medium"
                                                label={ reservation?.displayStatusValue }
                                                color={ getReservationStatusColor(reservation?.status) }
                                                sx={ { fontWeight: 600 } }
                                            />
                                        </Grid>
                                    </DialogTitle>
                                    <Divider/>

                                    <DialogContent sx={ { paddingTop: 2 } }>
                                        <Grid container spacing={ 3 }>
                                            <Grid item xs={ 12 }>
                                                <Grid container spacing={ 4 }>
                                                    <Grid item xs={ 12 } sm={ 4 }>
                                                        <RadioGroupFormik
                                                            name="allPositions"
                                                            label="Wszystkie pozycje"
                                                            options={ yesNoOptions }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={ 12 } sm={ 8 }>
                                                        <SelectFieldFormik
                                                            size="small"
                                                            name="reason"
                                                            label="Powód zwrotu"
                                                            options={ getReturnReasonOptions }
                                                            required
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            { !formik?.values?.allPositions && (
                                                <Grid item xs={ 12 }>
                                                    <Typography variant="subtitle2" color="textSecondary" sx={ {
                                                        textTransform: 'uppercase',
                                                        fontWeight: 600,
                                                        mb: 2
                                                    } }>
                                                        Wybierz produkty do zwrotu
                                                    </Typography>
                                                    <FieldArray
                                                        name={ 'positions' }
                                                        render={ () => (
                                                            <TableContainer component={ Paper } variant="outlined">
                                                                <Table stickyHeader size="small">
                                                                    <TableHead>
                                                                        <TableRow sx={ {
                                                                            '& th': {
                                                                                backgroundColor: 'slate.50',
                                                                                fontWeight: 600
                                                                            }
                                                                        } }>
                                                                            <TableCell>Nazwa produktu</TableCell>
                                                                            <TableCell align="center">Cena
                                                                                sprzedaży</TableCell>
                                                                            <TableCell align="center">Oryginalna liczba
                                                                                sztuk</TableCell>
                                                                            <TableCell align="center">Skorygowana liczba
                                                                                sztuk</TableCell>
                                                                            <TableCell align="center">Status</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        { formik?.values?.positions?.map((position, index) => (
                                                                            <TableRow key={ index } hover>
                                                                                <TableCell sx={ {
                                                                                    textDecoration: position?.isReturned ? 'line-through' : 'none',
                                                                                    fontWeight: 500
                                                                                } }>
                                                                                    { position?.productName }
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    { position?.sellingPrice }
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    { position?.quantity }
                                                                                </TableCell>
                                                                                <TableCell align="center"
                                                                                           sx={ { width: 150 } }>
                                                                                    { !position?.isReturned ? (
                                                                                        <TextFieldFormik
                                                                                            name={ `positions.${ index }.newQuantity` }
                                                                                            size="small"
                                                                                            type="number"
                                                                                            inputProps={ {
                                                                                                min: '0',
                                                                                                step: '1',
                                                                                                max: position?.quantity
                                                                                            } }
                                                                                            sx={ { background: 'white' } }
                                                                                        />
                                                                                    ) : '-' }
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    <Chip
                                                                                        label={ position?.isReturned ? "Zwrócony" : "Dostępny do zwrotu" }
                                                                                        color={ position?.isReturned ? "warning" : "success" }
                                                                                        size="small"
                                                                                    />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )) }
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        ) }
                                                    />
                                                </Grid>
                                            ) }
                                        </Grid>
                                    </DialogContent>
                                </>
                            ) }
                            <DialogActions sx={ { p: 2, borderTop: theme => `1px solid ${ theme.palette.divider }` } }>
                                <Button onClick={ onClose } variant="outlined">Zamknij</Button>
                                <SubmitButton isLoading={ returnCustomerReservation?.loading }>
                                    Zapisz zwrot
                                </SubmitButton>
                            </DialogActions>
                        </Box>
                    }
                </Formik>
            </Dialog>
        </>
    );
};

export default ReturnReservationDialog;